import { useQueries } from '@tanstack/react-query'
import { Select, Button, notification, Typography } from 'antd'
import dayjs from 'dayjs'
import { EditOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import {
  fetchOperatorAdditiveMaterialsByDwhIds,
  getServiceProviderMaterialsByOperatorMateriaId,
} from '$api/evoAPIs'
import { PartNoBg } from '$pages/serviceuser/Marketplace/Bookservice/Defaultwizard/styledComponents'
import BookedServiceLabels from '$components/Label/BookedServiceLabels'
import PartFileDownloadButton from '$components/FileDownload/PartFileDownloadButton'
import TruncateText from '$components/TruncateText'
import { StyledTable } from '$pages/serviceprovider/ReceivedRequests/CreateOffersView/styledComponents'
import PropTypes from 'prop-types'

const ForwardingPartsTable = ({
  orderParts,
  mainOrder,
  subOrders,
  selectedProviderName,
  materialSelections,
  setMaterialSelections,
  operatorMaterialSelections,
  setOperatorMaterialSelections,
  next,
  isForwarded,
}) => {
  const { t } = useTranslation()
  const { Text } = Typography

  const operatorMaterialResults = useQueries({
    queries: orderParts.map((orderPart) => ({
      queryKey: ['operatorMaterialsByDwh', [orderPart.dwh_material]],
      queryFn: fetchOperatorAdditiveMaterialsByDwhIds,
      enabled: !!orderPart.dwh_material,
    })),
  })

  const serviceProviderMaterialResults = useQueries({
    queries: orderParts.map((orderPart) => {
      const selectedOperatorMaterialId =
        operatorMaterialSelections[orderPart.id]
      const selectedColor = orderPart.selected_color
      return {
        queryKey: [
          selectedProviderName,
          selectedOperatorMaterialId,
          selectedColor,
        ],
        queryFn: ({
          queryKey: [service_provider_name, operator_material_id],
        }) =>
          getServiceProviderMaterialsByOperatorMateriaId({
            queryKey: [
              service_provider_name,
              operator_material_id,
              selectedColor,
            ],
          }),
        enabled: !!selectedProviderName && !!selectedOperatorMaterialId,
      }
    }),
  })

  const orderPartsWithMaterials = orderParts.map((op, index) => ({
    ...op,
    operatorMaterials: operatorMaterialResults[index].data || [],
    operatorMaterialsLoading: operatorMaterialResults[index].isLoading,
    serviceProviderMaterials: serviceProviderMaterialResults[index].data || [],
    serviceProviderMaterialsLoading:
      serviceProviderMaterialResults[index].isLoading,
  }))

  const handleMaterialChange = (id, value) => {
    const selectedOperatorMaterialId = operatorMaterialSelections[id]
    if (selectedOperatorMaterialId && value) {
      const currentRecord = orderPartsWithMaterials.find(
        (part) => part.id === id,
      )
      const selectedServiceProviderMaterial =
        currentRecord?.serviceProviderMaterials?.find(
          (material) => material.id === value,
        )

      if (
        !selectedServiceProviderMaterial?.mapped_operator_additive_material_ids?.includes(
          selectedOperatorMaterialId,
        )
      ) {
        notification.warning({
          message: t('operator_orders.unmapped_spam_warning'),
        })
      }
    }
    setMaterialSelections((prev) => ({ ...prev, [id]: value }))
  }

  const handleOperatorMaterialChange = (orderPartId, operatorMaterialId) => {
    setOperatorMaterialSelections((prev) => ({
      ...prev,
      [orderPartId]: operatorMaterialId,
    }))
    setMaterialSelections((prev) => ({
      ...prev,
      [orderPartId]: null,
    }))
  }

  const columns = [
    {
      title: t('Part No'),
      dataIndex: ['part', 'id_part_client'],
      render: (text) => <PartNoBg>{text}</PartNoBg>,
      width: 140,
    },
    {
      title: t('Part Name'),
      dataIndex: ['part', 'name'],
      render: (text) => <TruncateText text={text} />,
      ellipsis: true,
      width: 150,
    },
    {
      title: t('Amount'),
      dataIndex: 'amount',
      width: 70,
    },
    {
      title: t('operator_orders.selected_color_header'),
      width: 100,
      render: (record) =>
        record.selected_color
          ? t(
              `my_materials.colors.${record.selected_color.toLowerCase()}`,
              record.selected_color,
            )
          : '-',
    },
    {
      title: t('operator_additive_materials.header'),
      width: 180,
      render: (record) => {
        let availableOperatorMaterials = record.operatorMaterials || []

        if (isForwarded && record.operator_additive_material) {
          const alreadyInList = availableOperatorMaterials.some(
            (m) => m.id === record.operator_additive_material.id,
          )
          if (!alreadyInList) {
            availableOperatorMaterials = [
              ...availableOperatorMaterials,
              record.operator_additive_material,
            ]
          }
        }

        return (
          <div onClick={(e) => e.stopPropagation()}>
            <Select
              style={{ width: '100%' }}
              value={operatorMaterialSelections[record.id] || null}
              placeholder={t('Select Operator Material')}
              onChange={(val) => handleOperatorMaterialChange(record.id, val)}
              disabled={isForwarded}
              loading={record.operatorMaterialsLoading}
              options={availableOperatorMaterials.map((m) => ({
                label: m.material_name,
                value: m.id,
              }))}
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? '')
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            />
          </div>
        )
      },
    },
    {
      title: t('service_provider_additive_materials.header'),
      width: 180,
      render: (record) => {
        let filteredMaterials = record.serviceProviderMaterials
          ? record.serviceProviderMaterials
          : []
        if (isForwarded && record.service_provider_material) {
          const alreadyInList = filteredMaterials.some(
            (m) => m.id === record.service_provider_material.id,
          )
          if (!alreadyInList) {
            filteredMaterials = [
              ...filteredMaterials,
              record.service_provider_material,
            ]
          }
        }

        return (
          <div onClick={(e) => e.stopPropagation()}>
            <Select
              style={{ width: '100%' }}
              value={materialSelections[record.id] || null}
              placeholder={
                selectedProviderName
                  ? t('operator_orders.select_material')
                  : t('operator_orders.select_provider')
              }
              onChange={(val) => handleMaterialChange(record.id, val)}
              disabled={!selectedProviderName || isForwarded}
              options={filteredMaterials.map((m) => ({
                label: m.material_name,
                value: m.id,
              }))}
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? '')
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            />
          </div>
        )
      },
    },
    String(selectedProviderName || '')
      .toLowerCase()
      .includes('makerverse') && {
      title: t('Labels'),
      render: (record) => <BookedServiceLabels bookedServiceId={record.id} />,
      width: 100,
    },
    {
      title: '',
      key: 'files',
      render: (record) => (
        <PartFileDownloadButton
          fileName={record.user_file_location}
          shape="round"
          size="small"
        >
          {t('operator_orders.download_3d_model')}
        </PartFileDownloadButton>
      ),
      width: 100,
    },
    String(selectedProviderName).includes('makerverse') && {
      title: t('Action'),
      width: 100,
      render: (record) => (
        <div onClick={(e) => e.stopPropagation()}>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => next(record.id)}
          >
            {t('Offer')}
          </Button>
        </div>
      ),
    },
  ].filter(Boolean)

  const expandedRowRender = (orderPart) => {
    let relevantSubOrders

    if (subOrders?.length) {
      relevantSubOrders = subOrders
        .map((subOrder, index) => ({
          key: index,
          quantity: subOrder.orderpart_quantity?.[orderPart.id],
          desired_delivery_date: subOrder.desired_delivery_date,
        }))
        .filter((row) => row.quantity != null)
    } else {
      // no suborders — fall back to main order
      relevantSubOrders = [
        {
          key: 0,
          quantity: orderPart.amount,
          desired_delivery_date: mainOrder?.desired_delivery_date,
        },
      ]
    }

    const subOrderColumns = [
      {
        title: t('operator_orders.batch_number'),
        width: 120,
        render: (_, __, index) => (
          <Text style={{ fontSize: '12px' }}>{index + 1}</Text>
        ),
      },
      {
        title: t('received_requests.amount'),
        dataIndex: 'quantity',
        width: 100,
        render: (val) => <Text style={{ fontSize: '12px' }}>{val}</Text>,
      },
      {
        title: t('received_requests.desired_delivery_date'),
        dataIndex: 'desired_delivery_date',
        render: (val) =>
          val ? (
            <Text style={{ fontSize: '12px' }}>
              {dayjs(val).format('DD/MM/YYYY')}
            </Text>
          ) : (
            <Text type="secondary">-</Text>
          ),
      },
    ]

    return (
      <StyledTable
        columns={subOrderColumns}
        dataSource={relevantSubOrders}
        pagination={false}
        size="small"
        style={{ margin: '0 0 8px 8px' }}
      />
    )
  }

  return (
    <StyledTable
      columns={columns}
      dataSource={orderPartsWithMaterials}
      pagination={false}
      rowKey="id"
      size="small"
      expandable={{
        expandedRowRender,
        rowExpandable: () => true,
        expandRowByClick: true,
      }}
    />
  )
}

ForwardingPartsTable.propTypes = {
  mainOrder: PropTypes.shape({
    desired_delivery_date: PropTypes.string,
  }).isRequired,
  subOrders: PropTypes.array,
}

export default ForwardingPartsTable
